import { ReportReason } from "@cfg/api-contract";
import { buildConfiguratorSearch } from "@cfg/configurator-ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router";
import { useApp } from "../app-context.tsx";
import { useAuthUser } from "../auth.ts";
import { DefaultEmptyState } from "./defaults.tsx";

/** One published configuration: what it fits, who printed it, its params, and a way into the configurator. */
export function CommunityItemPage() {
  const { id = "" } = useParams();
  const { api, auth, brand, copy, registry } = useApp();
  const user = useAuthUser(auth);
  const qc = useQueryClient();
  const Empty = brand.slots?.EmptyState ?? DefaultEmptyState;
  const q = useQuery({ queryKey: ["public", id], queryFn: () => api!.getPublic(id), enabled: !!api });
  const catalog = useQuery({ queryKey: ["catalog", brand.config.id], queryFn: () => registry.catalog() });
  const [confirmMsg, setConfirmMsg] = useState<string | null>(null);
  const [reporting, setReporting] = useState(false);
  const [reason, setReason] = useState<ReportReason>("wrong-fit");
  const [note, setNote] = useState("");
  const [reportMsg, setReportMsg] = useState<string | null>(null);

  if (!api) return <Empty title={copy("community.title")} body="This deployment has no API configured." />;
  if (q.isLoading) return <p className="muted page">Loading…</p>;
  if (q.isError || !q.data) return <Empty title="This entry is not available" body="It may have been made private or removed." />;
  const p = q.data;
  const designTitle = catalog.data?.find((d) => d.id === p.designId)?.title ?? p.designId;
  const openHref = `/design/${encodeURIComponent(p.designId)}${buildConfiguratorSearch({ version: p.version, params: p.params })}`;

  const confirm = async () => {
    if (!user) return void auth.signIn();
    try {
      const r = await api.confirmPublic(p.id);
      setConfirmMsg(copy("community.confirmed"));
      qc.setQueryData(["public", id], { ...p, confirmCount: r.confirmCount });
    } catch (err) {
      setConfirmMsg((err as Error).message);
    }
  };
  const report = async () => {
    try {
      await api.reportPublic(p.id, { reason, note: note.trim() || undefined });
      setReportMsg(copy("community.reported"));
      setReporting(false);
    } catch (err) {
      setReportMsg((err as Error).message);
    }
  };

  return (
    <section className="page page-community-item">
      <header className="page-head">
        <h1>{p.name}</h1>
        <p className="page-sub">
          {designTitle} · v{p.version} · {copy("community.by", { name: p.authorName })} · {new Date(p.updatedAt).toLocaleDateString()}
        </p>
      </header>

      {p.fits.length > 0 && (
        <ul className="cfg-fit-list community-fits">
          {p.fits.map((f) => (
            <li key={f.ref} className="cfg-fit-chip">
              <a href={`/community?fit=${encodeURIComponent(f.ref)}`}>{copy("fits.chip", { label: brand.config.fitTags.label, ref: f.ref })}</a>
              {f.name && <span>{f.name}</span>}
            </li>
          ))}
        </ul>
      )}

      <p className="community-signals">
        {p.printed && <span className="version-pill">✓ {copy("community.printed")}</span>}
        {p.confirmCount > 0 && <span className="version-pill">{copy("community.confirms", { count: p.confirmCount })}</span>}
      </p>

      {p.notes && <p className="community-notes">{p.notes}</p>}

      <table className="community-params">
        <tbody>
          {Object.entries(p.params).map(([k, v]) => (
            <tr key={k}>
              <th scope="row">{k}</th>
              <td>{typeof v === "object" ? JSON.stringify(v) : String(v)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="panel-save-row community-actions">
        <a className="cfg-btn cfg-btn-primary" href={openHref}>
          {copy("community.open")}
        </a>
        <button type="button" className="cfg-btn" onClick={confirm}>
          {copy("community.confirm")}
        </button>
        {user && (
          <button type="button" className="cfg-chip" onClick={() => setReporting((r) => !r)}>
            {copy("community.report")}
          </button>
        )}
      </div>
      {confirmMsg && <p className="muted small">{confirmMsg}</p>}

      {reporting && (
        <form
          className="community-report"
          onSubmit={(e) => {
            e.preventDefault();
            void report();
          }}
        >
          <select className="cfg-input" value={reason} onChange={(e) => setReason(e.target.value as ReportReason)} aria-label="Reason">
            {ReportReason.options.map((r) => (
              <option key={r} value={r}>
                {r.replace("-", " ")}
              </option>
            ))}
          </select>
          <input className="cfg-input" maxLength={300} placeholder="Details (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
          <button type="submit" className="cfg-btn">
            {copy("community.report")}
          </button>
        </form>
      )}
      {reportMsg && <p className="muted small">{reportMsg}</p>}
    </section>
  );
}
